import React, { useState, useEffect } from 'react';
import axios from 'axios';

function Booking() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    date: '',
    destination: '',
    message: '',
    packageName: ''
  });
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loadingPackages, setLoadingPackages] = useState(true);

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      setLoadingPackages(true);
      const response = await axios.get('https://travel-agency-server-lilac.vercel.app/api/packages');
      if (response.data.success && response.data.packages) {
        const activePackages = response.data.packages
          .filter(pkg => pkg.isActive === true)
          .sort((a, b) => a.order - b.order);
        setPackages(activePackages);
      }
    } catch (error) {
      console.error('Error loading packages:', error);
      setPackages([
        { _id: '1', name: 'Bangladesh Cultural Tour' },
        { _id: '2', name: 'Sundarbans Adventure' },
        { _id: '3', name: 'Cox\'s Bazar Beach Vacation' }
      ]);
    } finally {
      setLoadingPackages(false);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim() || !formData.email.trim() ||
      !formData.date.trim() || !formData.destination.trim()) {
      setMessage({
        type: 'error',
        text: 'Please fill in all required fields'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessage({
        type: 'error',
        text: 'Please enter a valid email address'
      });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: '', text: '' });

      // Get package name
      const selectedPackage = packages.find(pkg => pkg._id === formData.destination);
      const packageName = selectedPackage ? selectedPackage.name : formData.destination;

      const bookingData = {
        ...formData,
        packageName: packageName,
        status: 'pending',
        createdAt: new Date().toISOString()
      };

      const response = await axios.post('https://travel-agency-server-lilac.vercel.app/api/bookings', bookingData);

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'Booking submitted successfully! We will contact you soon.'
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          date: '',
          destination: '',
          message: '',
          packageName: ''
        });
      } else {
        setMessage({
          type: 'error',
          text: response.data.error || 'Failed to submit booking. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Network error. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Header */}
      <div className="container-fluid bg-primary py-5 mb-5 hero-header">
        <div className="container py-5">
          <div className="row justify-content-center py-5">
            <div className="col-lg-10 pt-lg-5 mt-lg-5 text-center">
              <h1 className="display-3 text-white animated slideInDown">Booking</h1>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb justify-content-center">
                  <li className="breadcrumb-item">
                    <a href="/">Home</a>
                  </li>
                  <li
                    className="breadcrumb-item text-white active"
                    aria-current="page"
                  >
                    Booking
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Process Start */}
      <div className="container-xxl py-5">
        <div className="container">
          <div className="text-center pb-4 wow fadeInUp" data-wow-delay="0.1s">
            <h6 className="section-title bg-white text-center text-primary px-3">
              Process
            </h6>
            <h1 className="mb-5">3 Easy Steps</h1>
          </div>
          <div className="row gy-5 gx-4 justify-content-center">
            <div
              className="col-lg-4 col-sm-6 text-center pt-4 wow fadeInUp"
              data-wow-delay="0.1s"
            >
              <div className="position-relative border border-primary pt-5 pb-4 px-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center bg-primary rounded-circle position-absolute top-0 start-50 translate-middle shadow"
                  style={{ width: 100, height: 100 }}
                >
                  <i className="fa fa-globe fa-3x text-white" />
                </div>
                <h5 className="mt-4">Choose A Destination</h5>
                <hr className="w-25 mx-auto bg-primary mb-1" />
                <hr className="w-50 mx-auto bg-primary mt-0" />
                <p className="mb-0">
                  Browse our extensive collection of destinations worldwide.
                  From tropical paradises to historical landmarks, find the
                  perfect spot for your next adventure that matches your interests.
                </p>
              </div>
            </div>
            <div
              className="col-lg-4 col-sm-6 text-center pt-4 wow fadeInUp"
              data-wow-delay="0.3s"
            >
              <div className="position-relative border border-primary pt-5 pb-4 px-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center bg-primary rounded-circle position-absolute top-0 start-50 translate-middle shadow"
                  style={{ width: 100, height: 100 }}
                >
                  <i className="fa fa-sign fa-3x text-white" />
                </div>
                <h5 className="mt-4">Quality of Tour</h5>
                <hr className="w-25 mx-auto bg-primary mb-1" />
                <hr className="w-50 mx-auto bg-primary mt-0" />
                <p className="mb-0">
                  Experience premium travel with our carefully curated tours.
                  We ensure high-quality accommodations, expert guides, and
                  seamless itineraries for a memorable and comfortable journey.
                </p>
              </div>
            </div>
            <div
              className="col-lg-4 col-sm-6 text-center pt-4 wow fadeInUp"
              data-wow-delay="0.5s"
            >
              <div className="position-relative border border-primary pt-5 pb-4 px-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center bg-primary rounded-circle position-absolute top-0 start-50 translate-middle shadow"
                  style={{ width: 100, height: 100 }}
                >
                  <i className="fa fa-phone fa-3x text-white" />
                </div>
                <h5 className="mt-4">Contact With Us</h5>
                <hr className="w-25 mx-auto bg-primary mb-1" />
                <hr className="w-50 mx-auto bg-primary mt-0" />
                <p className="mb-0">
                  Get in touch with our travel experts for personalized assistance.
                  We provide 24/7 customer support to answer your queries and
                  help customize your perfect travel experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Process End */}

      {/* Message Alert */}
      {message.text && (
        <div className="container-xxl">
          <div className={`alert alert-${message.type} alert-dismissible fade show mx-3`}>
            <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'
              } me-2`}></i>
            {message.text}
            <button
              type="button"
              className="btn-close"
              onClick={() => setMessage({ type: '', text: '' })}
            ></button>
          </div>
        </div>
      )}

      {/* Booking Start */}
      <div className="container-xxl py-5 wow fadeInUp" data-wow-delay="0.1s">
        <div className="container">
          <div className="booking p-5" style={{
            backgroundImage: 'url(/assets/img/booking.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative'
          }}>
            {/* Overlay for better readability */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              zIndex: 1
            }}></div>

            <div className="row g-5 align-items-center position-relative" style={{ zIndex: 2 }}>
              <div className="col-md-6 text-white">
                <h6 className="text-warning text-uppercase">Booking</h6>
                <h1 className="text-white mb-4">Online Booking</h1>
                <p className="mb-4 text-white">
                  Planning your next adventure? Book with us for a seamless travel experience.
                  Our easy online booking system lets you reserve your dream tour in just a few clicks.
                </p>
                <p className="mb-4 text-white">
                  From tropical beaches to mountain treks, we offer curated packages that
                  cater to every traveler's needs. Secure your spot today and get ready
                  to create unforgettable memories with our expert guides and premium services.
                </p>
                <a className="btn btn-outline-warning py-3 px-5 mt-2" href="/packages">
                  View Packages
                </a>
              </div>
              <div className="col-md-6">
                <h1 className="text-white mb-4">Book A Tour</h1>
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control bg-dark bg-opacity-75 text-white border-white"
                          id="name"
                          placeholder="Your Name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                        <label htmlFor="name" className="text-white">Your Name *</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="email"
                          className="form-control bg-dark bg-opacity-75 text-white border-white"
                          id="email"
                          placeholder="Your Email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                        <label htmlFor="email" className="text-white">Your Email *</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="date"
                          className="form-control bg-dark bg-opacity-75 text-white border-white"
                          id="date"
                          placeholder="Preferred Date"
                          value={formData.date}
                          onChange={handleInputChange}
                          required
                          min={new Date().toISOString().split('T')[0]}
                        />
                        <label htmlFor="date" className="text-white">Preferred Date *</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating">
                        <select
                          className="form-select bg-dark bg-opacity-75 text-white border-white"
                          id="destination"
                          value={formData.destination}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="" className="bg-dark">Select Destination</option>
                          {loadingPackages ? (
                            <option value="" className="bg-dark" disabled>Loading packages...</option>
                          ) : (
                            packages.map((pkg) => (
                              <option key={pkg._id} value={pkg._id} className="bg-dark">
                                {pkg.name}
                              </option>
                            ))
                          )}
                        </select>
                        <label htmlFor="destination" className="text-white">Select Package *</label>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-floating">
                        <textarea
                          className="form-control bg-dark bg-opacity-75 text-white border-white"
                          placeholder="Special Request"
                          id="message"
                          style={{ height: 100 }}
                          value={formData.message}
                          onChange={handleInputChange}
                        />
                        <label htmlFor="message" className="text-white">Special Request</label>
                      </div>
                    </div>
                    <div className="col-12">
                      <button
                        className="btn btn-warning w-100 py-3"
                        type="submit"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Processing...
                          </>
                        ) : (
                          'Book Now'
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Booking End */}
    </div>
  );
}

export default Booking;